/**
 * Builds a full display name from separate name parts.
 * Safely handles empty strings, undefined, and null.
 *
 * @example
 * buildFullName('Sara', '', 'Ahmed')         // → "Sara Ahmed"
 * buildFullName('John', 'William', 'Doe')    // → "John William Doe"
 * buildFullName('Cher', '', '')              // → "Cher"
 */
export function buildFullName(
    firstName?: string | null,
    middleName?: string | null,
    lastName?: string | null
): string {
    return [firstName, middleName, lastName]
        .filter(Boolean)
        .join(' ');
}

/** Formats a display name without changing its stored value. */
export function formatNameTitleCase(name?: string | null): string {
    return (name ?? '')
        .replace(/[_-]+/g, ' ')
        .trim()
        .toLowerCase()
        .replace(/\b\w/g, character => character.toUpperCase());
}

/**
 * Builds display name from a patient API response object.
 * Falls back to legacy PatientName only if the new fields are absent.
 */
export function buildPatientDisplayName(patient: {
    PatientFirstName?: string;
    PatientMiddleName?: string;
    PatientLastName?: string;
    PatientName?: string;
    name?: string;
}): string {
    if (patient?.PatientFirstName) {
        return buildFullName(
            patient.PatientFirstName,
            patient.PatientMiddleName,
            patient.PatientLastName
        );
    }
    // Fallback for legacy records that predate the split migration
    return patient?.PatientName || patient?.name || '';
}

/**
 * Builds display name from a team member API response object.
 * Falls back to legacy `name` field if the new fields are absent.
 */
export function buildMemberDisplayName(member: {
    first_name?: string;
    middle_name?: string;
    last_name?: string;
    name?: string;
}): string {
    if (member?.first_name) {
        return buildFullName(member.first_name, member.middle_name, member.last_name);
    }
    return member?.name || '';
}
